/**
 * Temporary Access Pass (TAP) Card Component
 *
 * Card component for managing Temporary Access Pass authentication method
 * with 4 configuration options:
 * 1. Default Lifetime (10-43200 minutes)
 * 2. Default Length (8-48 characters)
 * 3. One-Time Use toggle
 * 4. Maximum Lifetime (10-43200 minutes)
 *
 * TDD Cycle 8 COMPLETE - Full implementation with API integration
 */

import { useState, useEffect, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Switch,
  Button,
  Field,
  SpinButton,
  shorthands,
  SpinButtonOnChangeData,
  SpinButtonChangeEvent,
} from '@fluentui/react-components';
import { PasswordRegular } from '@fluentui/react-icons';
import { BaseAuthenticationMethodCard } from './BaseAuthenticationMethodCard';

const useStyles = makeStyles({
  configSection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
    ...shorthands.padding(tokens.spacingVerticalM, 0, 0, 0),
  },
  buttonGroup: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalS),
    ...shorthands.padding(tokens.spacingVerticalM, 0, 0, 0),
  },
  hintText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    ...shorthands.margin(tokens.spacingVerticalXS, 0, 0, 0),
  },
});

interface TemporaryAccessPassCardProps {
  tenantId: string;
  initialConfig?: Record<string, unknown>;
  onConfigChange?: (config: Record<string, unknown>) => void;
}

export const TemporaryAccessPassCard: React.FC<TemporaryAccessPassCardProps> = ({
  tenantId: _tenantId,
  initialConfig,
  onConfigChange,
}) => {
  const styles = useStyles();

  // State for form values
  const [isEnabled, setIsEnabled] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [defaultLifetime, setDefaultLifetime] = useState(60); // minutes
  const [defaultLength, setDefaultLength] = useState(8); // characters
  const [isUsableOnce, setIsUsableOnce] = useState(true);
  const [maximumLifetime, setMaximumLifetime] = useState(480); // minutes

  // Store initial values for cancel functionality
  const initialValuesRef = useRef({
    isEnabled: false,
    defaultLifetime: 60,
    defaultLength: 8,
    isUsableOnce: true,
    maximumLifetime: 480,
  });

  // Load configuration from initialConfig prop
  useEffect(() => {
    if (!initialConfig) return;

    // Extract state
    const enabled = initialConfig.state === 'enabled';
    setIsEnabled(enabled);

    // Extract configuration
    const config = initialConfig.configuration as Record<string, unknown> | undefined;
    if (!config) return;

    // Extract TAP settings
    const defaultLifetimeInMinutes = config.default_lifetime_in_minutes;
    if (typeof defaultLifetimeInMinutes === 'number') {
      setDefaultLifetime(defaultLifetimeInMinutes);
    }

    const defaultLengthValue = config.default_length;
    if (typeof defaultLengthValue === 'number') {
      setDefaultLength(defaultLengthValue);
    }

    const isUsableOnceValue = config.is_usable_once;
    if (typeof isUsableOnceValue === 'boolean') {
      setIsUsableOnce(isUsableOnceValue);
    }

    const maximumLifetimeInMinutes = config.maximum_lifetime_in_minutes;
    if (typeof maximumLifetimeInMinutes === 'number') {
      setMaximumLifetime(maximumLifetimeInMinutes);
    }

    // Store initial values for cancel functionality
    initialValuesRef.current = {
      isEnabled: enabled,
      defaultLifetime: typeof defaultLifetimeInMinutes === 'number' ? defaultLifetimeInMinutes : 60,
      defaultLength: typeof defaultLengthValue === 'number' ? defaultLengthValue : 8,
      isUsableOnce: typeof isUsableOnceValue === 'boolean' ? isUsableOnceValue : true,
      maximumLifetime: typeof maximumLifetimeInMinutes === 'number' ? maximumLifetimeInMinutes : 480,
    };
  }, [initialConfig]);

  const handleSave = () => {
    // Transform component state to API format
    const config = {
      state: isEnabled ? 'enabled' : 'disabled',
      configuration: {
        default_lifetime_in_minutes: defaultLifetime,
        default_length: defaultLength,
        is_usable_once: isUsableOnce,
        maximum_lifetime_in_minutes: maximumLifetime,
      },
    };

    // Call onConfigChange callback if provided
    if (onConfigChange) {
      onConfigChange(config);
    }

    // Update initial values after successful save
    initialValuesRef.current = {
      isEnabled,
      defaultLifetime,
      defaultLength,
      isUsableOnce,
      maximumLifetime,
    };

    setIsEditing(false);
  };

  const handleCancel = () => {
    // Revert to initial values
    setIsEnabled(initialValuesRef.current.isEnabled);
    setDefaultLifetime(initialValuesRef.current.defaultLifetime);
    setDefaultLength(initialValuesRef.current.defaultLength);
    setIsUsableOnce(initialValuesRef.current.isUsableOnce);
    setMaximumLifetime(initialValuesRef.current.maximumLifetime);

    setIsEditing(false);
  };

  const handleLifetimeChange = (_ev: SpinButtonChangeEvent, data: SpinButtonOnChangeData) => {
    if (data.value !== undefined && data.value !== null) {
      setDefaultLifetime(data.value);
    }
  };

  const handleLengthChange = (_ev: SpinButtonChangeEvent, data: SpinButtonOnChangeData) => {
    if (data.value !== undefined && data.value !== null) {
      setDefaultLength(data.value);
    }
  };

  const handleMaxLifetimeChange = (_ev: SpinButtonChangeEvent, data: SpinButtonOnChangeData) => {
    if (data.value !== undefined && data.value !== null) {
      setMaximumLifetime(data.value);
    }
  };

  return (
    <BaseAuthenticationMethodCard
      icon={<PasswordRegular />}
      title="Temporary Access Pass"
      description="Time-limited passcode for passwordless user registration and recovery."
      securityLevel={2}
      securityText="Fair security"
      isEnabled={isEnabled}
      onToggle={(checked) => setIsEnabled(checked)}
      showConfigureButton={!isEditing}
      onConfigure={() => setIsEditing(true)}
    >
      {/* Configuration section - shown when enabled and editing */}
      {isEnabled && isEditing && (
        <div className={styles.configSection}>
          {/* Default Lifetime */}
          <Field label="Default Lifetime (minutes)">
            <SpinButton
              value={defaultLifetime}
              onChange={handleLifetimeChange}
              min={10}
              max={43200}
              aria-label="Default Lifetime"
            />
            <div className={styles.hintText}>10-43200 minutes</div>
          </Field>

          {/* Default Length */}
          <Field label="Default Length (characters)">
            <SpinButton
              value={defaultLength}
              onChange={handleLengthChange}
              min={8}
              max={48}
              aria-label="Default Length"
            />
            <div className={styles.hintText}>8-48 characters</div>
          </Field>

          {/* One-Time Use */}
          <Field label="One-Time Use">
            <Switch
              checked={isUsableOnce}
              onChange={(_, data) => setIsUsableOnce(data.checked)}
              aria-label="One-Time Use"
            />
          </Field>

          {/* Maximum Lifetime */}
          <Field label="Maximum Lifetime (minutes)">
            <SpinButton
              value={maximumLifetime}
              onChange={handleMaxLifetimeChange}
              min={10}
              max={43200}
              aria-label="Maximum Lifetime"
            />
            <div className={styles.hintText}>10-43200 minutes</div>
          </Field>

          {/* Save/Cancel Buttons */}
          <div className={styles.buttonGroup}>
            <Button appearance="primary" onClick={handleSave}>
              Save
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
          </div>
        </div>
      )}

    </BaseAuthenticationMethodCard>
  );
};
